import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Button, Checkbox, Form, Loader } from "semantic-ui-react";
import styled from "styled-components";
import { t } from "../../../constant/translations";
import { reverse, routes } from "../../../routes";
import { device } from "../../../config";
import { BackButton } from "../../common/back-button";
import { CalendarInput } from "../../../components/common/calendar-input";
import { DeleteModal } from "../../../components/common/delete-modal";
import { ErrorWrap } from "../../../helpers/error";
import {
  createSurvey,
  deleteSurvey,
  fetchSurvey,
  updateSurvey,
} from "../../../redux/modules/survey";
import toast from "react-hot-toast";
import ApiClient from "../../../helpers/api-client";

const apiClient = new ApiClient();

const PageWrap = styled.div`
  padding: 16px 20px;

  @media ${device.desktop} {
    padding: 20px 28px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const PageTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #1c1917;
  margin: 0;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media ${device.desktop} {
    grid-template-columns: 1fr 260px;
  }
`;

const Card = styled.div`
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 14px;
`;

const CardHeader = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid #e7e5e4;
  font-size: 13px;
  font-weight: 600;
  color: #1c1917;
`;

const CardBody = styled.div`
  padding: 14px 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #44403c;
  margin-bottom: 8px;
`;

const AgendaItem = styled.div`
  border: 1px solid #e7e5e4;
  border-radius: 4px;
  margin-bottom: 10px;
  background: #fff;
`;

const AgendaItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fb;
  border-bottom: 1px solid #e7e5e4;
`;

const AgendaNum = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #a8a29e;
  flex-shrink: 0;
`;

const AgendaInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  outline: none;

  &::placeholder {
    color: #aaa;
    font-weight: 400;
  }
`;

const TypeSelect = styled.select`
  border: 1px solid #dde1e7;
  border-radius: 3px;
  font-size: 11px;
  padding: 2px 4px;
  color: #555;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 16px;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: #dc3545;
  }
`;

const AgendaBody = styled.div`
  padding: 10px 12px;
`;

const YesNoChips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Chip = styled.div`
  border: 1px solid #dde1e7;
  border-radius: 3px;
  padding: 3px 10px;
  font-size: 11px;
  color: #666;
  background: #f8f9fb;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const OptionNum = styled.span`
  font-size: 11px;
  color: #aaa;
  min-width: 18px;
`;

const OptionInput = styled.input`
  flex: 1;
  border: 1px solid #e7e5e4;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 12px;
  color: #333;
  outline: none;

  &:focus {
    border-color: #1a7fc1;
  }
`;

const AddOptionBtn = styled.button`
  background: none;
  border: none;
  color: #1a7fc1;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 0;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const ChooseNote = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #888;
  font-style: italic;
`;

const AddQuestionBtn = styled.button`
  width: 100%;
  border: 1px dashed #d6d3d1;
  border-radius: 4px;
  background: none;
  padding: 10px;
  font-size: 12px;
  color: #78716c;
  cursor: pointer;
  margin-bottom: 14px;

  &:hover {
    border-color: #1a7fc1;
    color: #1a7fc1;
    background: #f0f7fd;
  }
`;

const ActionsSidebar = styled.div``;

const ActionsCard = styled.div`
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  background: #fff;
  padding: 14px;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldError = styled.div`
  font-size: 11px;
  color: #dc3545;
  margin-top: 3px;
`;

const Divider = styled.div`
  height: 1px;
  background: #e7e5e4;
  margin: 4px 0;
`;

let nextId = 10;

function questionsFromApi(apiQuestions) {
  return (apiQuestions || []).map((q) => ({
    id: q.id,
    title: q.title,
    type: q.qtype,
    options: (q.options || []).map((val, i) => ({ id: i, val })),
  }));
}

function SurveyFormComponent({
  match,
  history,
  surveyData,
  loading,
  fetchSurvey,
  createSurvey,
  updateSurvey,
  deleteSurvey,
}) {
  const isEdit = Boolean(match.params.surveyId);
  const surveyId = match.params.surveyId
    ? parseInt(match.params.surveyId)
    : null;
  const blockId = parseInt(match.params.blockId);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    end_date: "",
  });

  const [questions, setQuestions] = useState([
    { id: 1, title: "", type: "yesno", options: [] },
  ]);

  const [saving, setSaving] = useState(false);
  const [isDraft, setIsDraft] = useState(true);
  const [sendNotifications, setSendNotifications] = useState(true);
  const [fieldErrors, setFieldErrors] = useState(new ErrorWrap(null));
  const [questionErrors, setQuestionErrors] = useState([]);

  useEffect(() => {
    if (isEdit && surveyId) fetchSurvey(surveyId);
  }, [surveyId]);

  useEffect(() => {
    if (isEdit && surveyData) {
      setFormData({
        title: surveyData.title || "",
        description: surveyData.description || "",
        end_date: surveyData.end_date ? surveyData.end_date.slice(0, 10) : "",
      });
      setQuestions(questionsFromApi(surveyData.questions));
      setIsDraft(surveyData.status === "draft");
    }
  }, [surveyData && surveyData.id]);

  const nid = useRef(nextId);

  const addQuestion = () => {
    setQuestions((qs) => [
      ...qs,
      { id: nid.current++, title: "", type: "yesno", options: [] },
    ]);
  };

  const deleteQuestion = (id) => {
    setQuestions((qs) => qs.filter((q) => q.id !== id));
  };

  const updateQuestion = (id, patch) => {
    setQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const handleTypeChange = (id, newType) => {
    updateQuestion(id, {
      type: newType,
      options: newType === "choose" ? [{ id: Date.now(), val: "" }] : [],
    });
  };

  const toNoon = (dateStr) => (dateStr ? `${dateStr}T12:00:00` : null);

  const buildPayload = () => ({
    ...formData,
    end_date: toNoon(formData.end_date),
    block: blockId,
    questions: questions.map((q, i) => ({
      ...(q.id && q.id < 10 ? {} : { id: q.id }),
      order: i,
      title: q.title,
      qtype: q.type,
      options: q.type === "choose" ? q.options.map((o) => o.val) : [],
    })),
  });

  const goToList = () =>
    history.push(
      reverse(routes.blockDetails, { blockId: match.params.blockId })
    );

  const applyErrors = (reduxAction) => {
    const err = ErrorWrap.fromError(reduxAction.error);
    setFieldErrors(err);
    setQuestionErrors(reduxAction.error?.questions || []);
    toast.error(err.getGlobalErrorMsg());
  };

  const handleSave = () => {
    setSaving(true);
    setFieldErrors(new ErrorWrap(null));
    setQuestionErrors([]);
    const payload = buildPayload();
    const saveAction = isEdit
      ? updateSurvey({ id: surveyId, ...payload })
      : createSurvey(payload);
    saveAction
      .then((reduxAction) => {
        if (reduxAction?.type?.endsWith("_ERROR")) {
          setSaving(false);
          applyErrors(reduxAction);
          return;
        }
        if (isDraft) {
          setSaving(false);
          goToList();
          return;
        }
        const id = isEdit ? surveyId : reduxAction?.result?.id;
        if (!id) {
          setSaving(false);
          toast.error(t("survey.missing_id_error"));
          return;
        }
        apiClient
          .post(`/api/survey/${id}/publish/`, {
            data: { skip_notifications: !sendNotifications },
          })
          .then(() => {
            setSaving(false);
            goToList();
          })
          .catch((err) => {
            setSaving(false);
            toast.error(ErrorWrap.fromError(err).getGlobalErrorMsg());
          });
      })
      .catch((err) => {
        setSaving(false);
        toast.error(ErrorWrap.fromError(err).getGlobalErrorMsg());
      });
  };

  const handleDelete = (survey) => {
    deleteSurvey(survey.id).then(() => goToList());
  };

  const title = isEdit ? t("survey.edit") : t("survey.new");

  return (
    <PageWrap>
      <PageHeader>
        <BackButton size="tiny" />
        <PageTitle>{title}</PageTitle>
      </PageHeader>

      <Layout>
        <div>
          <Card>
            <CardHeader>{t("survey.basic_info")}</CardHeader>
            <CardBody>
              <Form>
                <Form.Field error={fieldErrors.has("title")}>
                  <label>{t("survey.topic")} *</label>
                  <Form.Input
                    value={formData.title}
                    placeholder={t("survey.topic")}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                  {fieldErrors.has("title") && (
                    <FieldError>{fieldErrors.get("title")}</FieldError>
                  )}
                </Form.Field>
                <Form.Field error={fieldErrors.has("description")}>
                  <label>{t("survey.description")}</label>
                  <Form.TextArea
                    value={formData.description}
                    rows={3}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                  />
                  {fieldErrors.has("description") && (
                    <FieldError>{fieldErrors.get("description")}</FieldError>
                  )}
                </Form.Field>
                <Form.Field error={fieldErrors.has("end_date")}>
                  <CalendarInput
                    name="end_date"
                    label={`${t("survey.end_date")} *`}
                    value={formData.end_date}
                    onChange={(e) =>
                      setFormData({ ...formData, end_date: e.target.value })
                    }
                  />
                  {fieldErrors.has("end_date") && (
                    <FieldError>{fieldErrors.get("end_date")}</FieldError>
                  )}
                </Form.Field>
              </Form>
            </CardBody>
          </Card>

          <SectionLabel>{t("survey.questions")}</SectionLabel>

          {questions.map((q, qi) => (
            <AgendaItem key={q.id}>
              <AgendaItemHeader>
                <AgendaNum>{qi + 1}.</AgendaNum>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AgendaInput
                    value={q.title}
                    placeholder={t("survey.question_text")}
                    style={
                      questionErrors[qi]?.title
                        ? { borderBottom: "1px solid #dc3545" }
                        : undefined
                    }
                    onChange={(e) =>
                      updateQuestion(q.id, { title: e.target.value })
                    }
                  />
                  {questionErrors[qi]?.title && (
                    <FieldError>{questionErrors[qi].title[0]}</FieldError>
                  )}
                </div>
                <TypeSelect
                  value={q.type}
                  onChange={(e) => handleTypeChange(q.id, e.target.value)}
                >
                  <option value="yesno">
                    {t("survey.question_type_yesno")}
                  </option>
                  <option value="choose">
                    {t("survey.question_type_choose")}
                  </option>
                </TypeSelect>
                {questions.length > 1 && (
                  <DeleteBtn onClick={() => deleteQuestion(q.id)}>
                    &times;
                  </DeleteBtn>
                )}
              </AgendaItemHeader>

              <AgendaBody>
                {q.type === "yesno" ? (
                  <YesNoChips>
                    {[
                      `${t("survey.yes")} \u2713`,
                      `${t("survey.no")} \u2717`,
                      t("survey.abstain"),
                    ].map((o) => (
                      <Chip key={o}>{o}</Chip>
                    ))}
                  </YesNoChips>
                ) : (
                  <div>
                    {q.options.map((opt, oi) => (
                      <OptionRow key={opt.id}>
                        <OptionNum>{oi + 1}.</OptionNum>
                        <OptionInput
                          value={opt.val}
                          placeholder={`${t("survey.option_placeholder")} ${
                            oi + 1
                          }`}
                          onChange={(e) =>
                            updateQuestion(q.id, {
                              options: q.options.map((o) =>
                                o.id === opt.id
                                  ? { ...o, val: e.target.value }
                                  : o
                              ),
                            })
                          }
                        />
                        {q.options.length > 1 && (
                          <DeleteBtn
                            onClick={() =>
                              updateQuestion(q.id, {
                                options: q.options.filter(
                                  (o) => o.id !== opt.id
                                ),
                              })
                            }
                          >
                            &times;
                          </DeleteBtn>
                        )}
                      </OptionRow>
                    ))}
                    <AddOptionBtn
                      onClick={() =>
                        updateQuestion(q.id, {
                          options: [...q.options, { id: Date.now(), val: "" }],
                        })
                      }
                    >
                      + {t("survey.add_option")}
                    </AddOptionBtn>
                    <ChooseNote>
                      {t("survey.winner")} — {t("survey.winner_note_result")}
                    </ChooseNote>
                  </div>
                )}
              </AgendaBody>
            </AgendaItem>
          ))}

          <AddQuestionBtn onClick={addQuestion}>
            + {t("survey.add_question")}
          </AddQuestionBtn>
        </div>

        <ActionsSidebar>
          <ActionsCard>
            <ActionsRow>
              <Checkbox
                toggle
                label={t("survey.save_draft")}
                checked={isDraft}
                onChange={(_, data) => setIsDraft(data.checked)}
              />
              <Checkbox
                toggle
                disabled={isDraft}
                label={t("survey.send_notifications")}
                checked={sendNotifications}
                onChange={(_, data) => setSendNotifications(data.checked)}
              />
              <Divider />
              <Button primary fluid loading={saving} onClick={handleSave}>
                {t("labels.save")}
              </Button>
              {isEdit && surveyData && (
                <DeleteModal
                  entity={surveyData}
                  del={handleDelete}
                  floated={null}
                />
              )}
            </ActionsRow>
          </ActionsCard>
        </ActionsSidebar>
      </Layout>
    </PageWrap>
  );
}

const mapStateToProps = (state, ownProps) => {
  const surveyId = ownProps.match?.params?.surveyId
    ? parseInt(ownProps.match.params.surveyId)
    : null;
  return {
    surveyData: surveyId
      ? (state.survey.entities || []).find((s) => s.id === surveyId)
      : null,
    loading: state.survey.loading,
  };
};

export const SurveyForm = withRouter(
  connect(mapStateToProps, {
    fetchSurvey,
    createSurvey,
    updateSurvey,
    deleteSurvey,
  })(SurveyFormComponent)
);
